import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getWallets, setCurrentWallet } from '@/features/wallets/store/actions/wallet.actions';
import { IWallet } from '@/features/wallets/store/models/wallet.model';
import { UserWallets } from '@/features/wallets/components/UserWallets/UserWallets';
import { WalletCreation } from '@/features/wallets/components/WalletCreation';
import { WalletOperations } from '@/features/wallets/components/WalletOperations/WalletOperations';

import { selectProfileWalletsByUserId, selectWalletsByUserId } from '@/store/selectors';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { useProfileContext } from '@/features/context/ProfileContext';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { WALLETS_PER_PAGE } from '@/features/wallets/constants';

export const WalletsTab = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useProfileContext();
  const { walletId } = useParams();

  const isLoading = useAppSelector((state) => selectProfileWalletsByUserId(state, user.userId)?.isLoading);

  const wallets = useAppSelector((state) => selectWalletsByUserId(state, user.userId), shallowEqual);

  const hasWallets = !!wallets?.length;

  const hasActifWallets = useMemo(
    () =>
      !!wallets?.reduce(
        (acc: IWallet[], wallet) =>
          wallet?.authorizedBalance && wallet?.authorizedBalance > 0 ? [...acc, wallet] : acc,
        [],
      ).length,
    [wallets],
  );

  useEffect(() => {
    dispatch(getWallets(user.userId, true, 1, WALLETS_PER_PAGE));
  }, [user.userId, dispatch]);

  useEffect(() => {
    if (walletId) {
      dispatch(setCurrentWallet(user.userId, Number(walletId)));
    }
  }, [dispatch, walletId, user.userId]);

  useEffect(() => {
    if (hasWallets && !walletId) {
      navigate(DashboardHelpers.generateProfilePath(user.userId, { walletId: wallets?.[0]?.walletId }));
    }
  }, [hasWallets, user.userId, wallets, navigate, walletId]);

  return (
    <div className="flex flex-col">
      {!hasWallets && !isLoading ? (
        <WalletCreation user={user} />
      ) : (
        <div className="flex gap-5 mt-7">
          <UserWallets user={user} />
          {hasWallets && <WalletOperations hasActifWallets={hasActifWallets} />}
        </div>
      )}
    </div>
  );
};
